import { Download } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  useDownloadAttachmentMutation,
  useDownloadZipMutation,
} from '../../../../app/athenaApi';
import { HandlersAttachmentResponse } from '../../../../app/athenaApiGen';
import type { RootState } from '../../../../app/store';
import { streamZipToDisk } from '../../../../utils/streamZipToDisk';

type TDownloadsProps = {
  attachments?: HandlersAttachmentResponse[];
  zipFileName?: string;
};

const Downloads = (props: TDownloadsProps) => {
  // Hooks must run unconditionally — keep them above any early return so the
  // hook order is stable across renders (React rules-of-hooks).
  const [downloadAttachment] = useDownloadAttachmentMutation();
  const [fetchZip] = useDownloadZipMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const token = useSelector((state: RootState) => state.user.loginToken);

  if (props?.attachments?.length === 0) {
    return <></>;
  }

  const download = async (id: string) => {
    try {
      await downloadAttachment(id).unwrap();
    } catch (err) {
      console.error('Download failed:', err);
      setErrorMessage('Failed to download attachment. Please try again.');
    }
  };

  const downloadAll = async () => {
    // Only send attachments that actually carry an id. Mapping every
    // attachment's `attachmentId!` blindly sent `null` into the payload for
    // id-less records, which fails the backend's `uuid` validation and 400s the
    // whole bulk request. Mirrors the per-item render guard below.
    const ids =
      props?.attachments
        ?.filter((attachment) => attachment.attachmentId)
        .map((attachment) => attachment.attachmentId!) ?? [];
    if (ids.length === 0) {
      setErrorMessage('No downloadable attachments found.');
      return;
    }
    const fileName = props.zipFileName || 'athena_attachments';

    setIsDownloadingAll(true);
    try {
      // Primary path: stream the archive straight to disk (Chromium). The whole
      // ZIP is never held in memory, so large downloads no longer crash the tab.
      const streamed = await streamZipToDisk({
        url: '/api/attachments/download',
        method: 'POST',
        body: { attachmentIds: ids },
        suggestedName: fileName,
        token,
      });
      if (streamed) return;

      // Fallback: buffered blob download — fine for small archives and the only
      // option on browsers without the File System Access API (Firefox/Safari).
      await fetchZip({ ids, fileName }).unwrap();
    } catch (err) {
      // User dismissed the "Save As" dialog — not an error.
      if ((err as DOMException)?.name === 'AbortError') return;
      console.error('Bulk download failed:', err);
      setErrorMessage('Failed to download attachments. Please try again.');
    } finally {
      setIsDownloadingAll(false);
    }
  };
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        borderTop: `1px solid ${theme.palette.divider}`,
        pt: 1,
      })}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography>Attachments ({props?.attachments?.length ?? 0})</Typography>
        <Button
          size="small"
          disabled={isDownloadingAll}
          startIcon={
            isDownloadingAll ? (
              <CircularProgress size={16} sx={{ color: '#0092CB' }} />
            ) : (
              <Download sx={{ width: '20px !important' }} />
            )
          }
          sx={{
            color: '#0092CB',
          }}
          onClick={downloadAll}
        >
          {isDownloadingAll ? 'Downloading…' : 'Download All'}
        </Button>
      </Box>
      <List
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: 0,
        }}
      >
        {props?.attachments?.map(
          (attachment) =>
            attachment?.attachmentId && (
              <ListItem
                key={attachment?.attachmentId}
                sx={{
                  display: 'flex',
                  color: '#0092CB',
                  p: 0,
                  textDecorationLine: 'underline',
                  cursor: 'pointer',
                  minWidth: 0,
                }}
              >
                <ListItemText
                  onClick={() => download(attachment.attachmentId!)}
                  title={attachment?.name}
                  slotProps={{ primary: { noWrap: true } }}
                  sx={{ minWidth: 0 }}
                >
                  {attachment?.name}
                </ListItemText>
              </ListItem>
            ),
        )}
      </List>
      <Snackbar
        open={errorMessage !== null}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setErrorMessage(null)}
          variant="filled"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Downloads;
